// Public Support page. Lives outside the auth gate so it answers without
// sign-in (the App.tsx comment records an App Store dependency on this URL
// responding publicly — Apple Guideline 1.5) and so a pro looking for help
// can read it before they have ever signed in.
//
// REWRITTEN 2026-08-26: the previous content was a verbatim copy of
// support-web's PKNT Ops console page — the OWNER app's support page, with
// its Kira / Apple Intelligence / voice-mode troubleshooting, presented to
// pros under the wrong product name. This is the Pro Hub's own support page:
// IMAGE OS branding, magic-link help, and the studio's owner and their team as
// the first door, because a pro's studio owns their day-to-day answers. (There
// is no staffed reception to send anyone to — decision e66afc72.)
//
// Design intent: same minimal-prose layout as Privacy / Terms so the public
// surfaces share a visual identity. No auth chrome, no nav.
import { bareHost, inboxAddress, PRO_HUB_ORIGIN } from '@/lib/hosts';
import { PRODUCT_NAME, PRODUCT_TAGLINE } from '@/lib/brand';
import { TeneanLockup } from '@/components/TeneanLockup';

const SUPPORT = inboxAddress('support');

export default function Support() {
  return (
    <div className="min-h-screen bg-brand-fog">
      <header className="px-6 pt-12 pb-6 text-center border-b border-brand-mist">
        <a href="/" className="inline-block">
          <h1 className="flex justify-center" aria-label={PRODUCT_NAME}><TeneanLockup width={170} /></h1>
        </a>
        <p className="text-[10px] font-semibold tracking-[0.3em] text-brand-slate mt-1 uppercase">
          Pro Hub &middot; Support
        </p>
      </header>

      <main className="px-6 py-10 max-w-3xl mx-auto">
        <article className="prose prose-sm sm:prose-base prose-headings:font-display prose-headings:text-image-black prose-p:text-brand-slate prose-li:text-brand-slate prose-a:text-image-pink hover:prose-a:text-image-pink-dark">

          <p>
            The Pro Hub is where the studio's professionals run their day —
            your profile, your QR code, maintenance requests, documents, and your access
            code, at <a href={PRO_HUB_ORIGIN}>{bareHost(PRO_HUB_ORIGIN)}</a>. If something
            isn&rsquo;t working the way you expect, this page is the fastest way to get help.
          </p>

          <h2>Get help</h2>
          <ul>
            <li>
              <strong>Your studio&rsquo;s owner and their team</strong> are the first door for
              anything about your suite, your lease, events, or your profile — call or text
              the studio, and they can see and fix most things.
            </li>
            <li className="mt-3">
              <strong>Email:</strong>{' '}
              <a href={`mailto:${SUPPORT}`}>{SUPPORT}</a>
              <br />
              <span className="text-sm">
                For anything about the Pro Hub itself — a page that won&rsquo;t load, a
                sign-in that won&rsquo;t complete, something that looks wrong. Screenshots
                speed things up dramatically.
              </span>
            </li>
          </ul>

          <h2>Common questions</h2>

          <h3>How do I sign in?</h3>
          <p>
            Go to <a href={PRO_HUB_ORIGIN}>{bareHost(PRO_HUB_ORIGIN)}</a>, enter the email your
            studio has on file for you, and tap the link we send you. That&rsquo;s the whole
            sign-in — no password, nothing to remember.
          </p>

          <h3>The sign-in link didn&rsquo;t arrive</h3>
          <p>
            Check spam first. If it isn&rsquo;t there within a minute, request a fresh link —
            they expire after a few minutes. If it still doesn&rsquo;t arrive, the email you
            entered may not match the one your studio has on file: check with your studio&rsquo;s owner,
            or email <a href={`mailto:${SUPPORT}`}>{SUPPORT}</a> with the
            address you tried.
          </p>

          <h3>I signed in and my info isn&rsquo;t there</h3>
          <p>
            Sign out and back in once — your account links to your studio record on sign-in.
            If it still looks empty, your studio may have a different email on file for you:
            your studio&rsquo;s owner can confirm which address to use.
          </p>

          <h3>How do I delete my account?</h3>
          <p>
            Email <a href={`mailto:${SUPPORT}`}>{SUPPORT}</a> from the
            address tied to your account; we&rsquo;ll confirm the request and process the
            deletion within 30 days. See the <a href="/privacy">Privacy Policy</a> for the
            full data-handling details.
          </p>

          <h3>I found a security issue</h3>
          <p>
            Please report security issues privately to{' '}
            <a href={`mailto:${SUPPORT}`}>{SUPPORT}</a> with the subject
            line &ldquo;Security report&rdquo;. We aim to acknowledge security reports within
            24 hours. Don&rsquo;t post details publicly until we&rsquo;ve had a chance to
            address the issue.
          </p>

          <h2>Privacy &amp; terms</h2>
          <p>
            <a href="/privacy">Privacy Policy</a> &middot;{' '}
            <a href="/terms">Terms of Service</a>
          </p>

          <hr className="my-10 border-brand-mist" />

          <p className="text-xs text-brand-slate text-center">
            {PRODUCT_NAME} &middot; {PRODUCT_TAGLINE} &middot;{' '}
            <a href={`mailto:${SUPPORT}`}>{SUPPORT}</a>
          </p>
        </article>
      </main>

      <footer className="px-6 py-8 max-w-3xl mx-auto text-center border-t border-brand-mist">
        <a href="/" className="text-sm text-brand-slate hover:text-image-pink">
          &larr; Back to the Pro Hub
        </a>
      </footer>
    </div>
  );
}
